import { setupPath } from '@/config/businesses';
import type { BusinessProfile } from '@/types';

export interface ActiveBusinessResolution {
  /** The business the app works in, or null when there is none on this device. */
  business: BusinessProfile | null;
  /**
   * The stored id names a business that is not here — deleted, corrupted or
   * left over from another build — so the resolved one should be stored in its
   * place. Nothing stored yet is not damage: the first business is being
   * created, and creation writes the id itself.
   */
  needsRepair: boolean;
}

/**
 * Which business is active: the one `settings.activeBusinessId` names, else
 * the first in the repository's own order. Pure, so every edge — no
 * businesses, a missing id, a stale one, a non-string — is testable, and the
 * provider cannot disagree with its tests.
 */
export function resolveActiveBusiness(
  businesses: readonly BusinessProfile[],
  storedId: unknown,
): ActiveBusinessResolution {
  const first = businesses[0];
  if (!first) return { business: null, needsRepair: false };
  const match = typeof storedId === 'string' ? businesses.find((business) => business.id === storedId) : undefined;
  if (match) return { business: match, needsRepair: false };
  return { business: first, needsRepair: storedId !== undefined && storedId !== null };
}

/**
 * The business a homepage trade card stands for on this device, found by its
 * template id — the canonical link between a business and its trade, never
 * the display name. The active business if it is of that trade, otherwise the
 * first of that trade in the repository's order. Null: the trade is new here.
 */
export function businessForTemplate(
  businesses: readonly BusinessProfile[],
  templateId: string,
  activeId: string | null | undefined,
): BusinessProfile | null {
  const ofTrade = businesses.filter((business) => business.templateId === templateId);
  return ofTrade.find((business) => business.id === activeId) ?? ofTrade[0] ?? null;
}

export type HomepageChoice =
  /** A business of this trade is already here: open it, with everything it has. */
  | { kind: 'open'; business: BusinessProfile }
  /** Nothing of this trade yet, or "Add business" was asked for: set one up. */
  | { kind: 'setup'; path: string };

/**
 * What choosing a trade on the homepage does. The clicked card decides — the
 * business that happened to be active before never does. Only "Add business"
 * (`adding`) sets up a new business for a trade that already has one.
 */
export function resolveHomepageChoice({ businesses, activeId, templateId, adding }: {
  businesses: readonly BusinessProfile[];
  activeId: string | null | undefined;
  templateId: string;
  adding: boolean;
}): HomepageChoice {
  if (!adding) {
    const existing = businessForTemplate(businesses, templateId, activeId);
    if (existing) return { kind: 'open', business: existing };
  }
  return { kind: 'setup', path: setupPath(templateId, adding ? 'add' : businesses.length > 0 ? 'new' : 'first') };
}
